import { Clock3, CalendarClock } from 'lucide-react';
import { UserLink } from './EntityLinks';
import { RoleBadge } from './RoleBadge';
import { formatIst } from '../lib/time';
import { shiftHue } from '../lib/visitShifts';

/**
 * The fields the API attaches to a retailer / farmer about its registration (backend `attachRegisteredBy`).
 */
export interface Registration {
  createdBy?: string | number | null;
  createdByName?: string | null;
  createdByRole?: string | null;
  createdAt?: string | null;
  createdShiftSeq?: number | null;
  createdShiftDate?: string | null;
  createdShiftCount?: number | null;
  /** Farmers only — `IMPORT` names a row nobody registered by hand. */
  registeredVia?: string | null;
}

/**
 * WHICH SHIFT a dealer / farmer was registered in — its own pill, never a "· shift 3" tail.
 *
 * In the shift's own colour (the colour its box wears on My Field Day and in the attendance record), so
 * "Shift 3" here and Shift 3's box there read as the same thing. Added while not punched in (desk work,
 * or before shifts were recorded) → a neutral "Not on a shift" pill: a fact, not a warning.
 */
export function RegisteredShiftPill({ seq }: { seq?: number | null }) {
  if (!seq) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-surface-2 px-2 py-0.5 type-caption font-medium text-muted ring-1 ring-inset ring-line"
        title="Added while the person was not punched in">
        Not on a shift
      </span>
    );
  }
  const hue = shiftHue(seq);
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 type-caption font-medium"
      style={{ background: `${hue}1a`, color: hue, boxShadow: `inset 0 0 0 1px ${hue}55` }}
      title={`Added during shift ${seq} of that day`}>
      <Clock3 className="size-3" />
      Shift {seq}
    </span>
  );
}

/**
 * WHO REGISTERED THIS DEALER / FARMER — the same on every screen that shows it.
 *
 * Line 1: the person (opening their staff page) and their role badge.
 * Line 2: WHEN, with its own clock mark, and WHICH SHIFT as its own pill. Two facts, two marks — not a
 * sentence glued together with dots.
 * `compact` (register cells) drops the date — the register already has a Registered / Created column.
 * Nobody recorded: "Imported" for an import, otherwise "Not recorded" — never a blank that reads as
 * "not loaded yet", and never a guess.
 */
export function RegisteredBy({ r, compact = false }: { r: Registration; compact?: boolean }) {
  if (!r.createdByName) {
    return <span className="text-subtle">{r.registeredVia === 'IMPORT' ? 'Imported' : 'Not recorded'}</span>;
  }
  return (
    <span className="inline-flex min-w-0 flex-col gap-1">
      <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
        <UserLink id={r.createdBy} role={r.createdByRole}>{r.createdByName}</UserLink>
        {r.createdByRole ? <RoleBadge role={r.createdByRole} /> : null}
      </span>
      <span className="inline-flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
        {!compact && r.createdAt && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-normal text-muted">
            <CalendarClock className="size-3.5 shrink-0" />
            {formatIst(r.createdAt)}
          </span>
        )}
        <RegisteredShiftPill seq={r.createdShiftSeq} />
      </span>
    </span>
  );
}
